/**
 * The design as the phone will show it, in plain DOM (DESIGN §9.1, D4).
 *
 * This and `DesignRenderer.kt` implement the same document, and one set of
 * golden fixtures is run by both suites — that is what makes "what you see is
 * what you install" a property rather than a hope. Everything about the *look*
 * comes from the customer's spec as inline styles; the dashboard's own tokens
 * only draw the frame around it.
 *
 * A dp is a CSS pixel here: the frame is 360 dp wide, and callers scale the
 * whole thing with `transform` when they want a thumbnail. Text is `px`
 * matched to the phone's `sp` at font scale 1 — the phone honours the system
 * font scale and the preview says so in a footnote.
 *
 * The frame has the same two shapes `DesignRenderer` gives the screen: one
 * padded stack of blocks, or — once a `flex` module block is in the design —
 * a column of *runs*, with that block a child of the column and none of the
 * theme's padding around it (MODULES §7.4, W10).
 */

import { useRef, useState, type CSSProperties, type DragEvent, type ReactNode } from "react";

import {
  BLOCK_MIME,
  carriesBlock,
  carriesBlockType,
  draggedBlock,
  draggedPaletteItem,
  filesOf,
  hasFiles,
  slotAt,
  type DropTarget,
  type PaletteItem,
} from "./dnd";
import { IconModuleBlock } from "./icons";
import {
  BUTTON_HEIGHTS,
  DEFAULTS,
  MODULE_ACTION,
  MODULE_BLOCK,
  type Align,
  type Background,
  type Block,
  type Spec,
  type StatusRow,
} from "./spec";

/** Where a preview resolves an asset id to something an `<img>` can load. */
export type AssetUrls = Record<string, string>;

/** One status row as the preview shows it: `<module>.<value>` included. */
export interface SampleRow {
  label: string;
  value: string;
}

/**
 * As much of a module's block as the preview needs to stand in for it: what to
 * call it, and how much of the screen it takes (MODULES §2.11). Keyed
 * `<module>.<block>`, like the status samples.
 */
export interface SampleBlock {
  label: string;
  height: "content" | "flex";
}

/** What a `content` module block is drawn as, in dp — tall enough to read as
 *  a page and short enough to leave the design around it visible. */
const CONTENT_BLOCK_HEIGHT = 120;

/**
 * The module block that *is* the screen (MODULES §7.4, W10): the manifest
 * says `flex`, so the phone hands it the column itself — every pixel the
 * other blocks leave, full width, and none of the theme's padding on any
 * side.
 *
 * Without the catalogue there is no way to know, and `content` is the kinder
 * guess of the two: it keeps the rest of the design visible.
 */
function isFlexBlock(block: Block, sampleBlocks: Record<string, SampleBlock>): boolean {
  if (block.type !== MODULE_BLOCK) return false;
  return sampleBlocks[`${block.module}.${block.block}`]?.height === "flex";
}

/**
 * One child of the frame's column, once a design carries a `flex` block: a
 * run of consecutive ordinary blocks — `content` module blocks included —
 * which keeps the theme's padding and gaps, or the flex block itself, which
 * is laid out *as* the column rather than inside it.
 *
 * `at` is where the run's first block sits in the design, because a drop slot
 * counts blocks and not runs.
 */
type Run =
  | { kind: "run"; at: number; blocks: Block[] }
  | { kind: "flex"; at: number; block: Block };

/** `DesignRenderer.flexColumn` in a browser: the same runs, in the same order,
 *  and two adjacent flex blocks share what is left equally (`1 1 0` each, the
 *  phone's `weight 1`). */
function runsOf(blocks: readonly Block[], sampleBlocks: Record<string, SampleBlock>): Run[] {
  const runs: Run[] = [];
  for (const [at, block] of blocks.entries()) {
    if (isFlexBlock(block, sampleBlocks)) {
      runs.push({ kind: "flex", at, block });
      continue;
    }
    const last = runs[runs.length - 1];
    if (last?.kind === "run") last.blocks.push(block);
    else runs.push({ kind: "run", at, blocks: [block] });
  }
  return runs;
}

export const FRAME_WIDTH = 360;
/** 360×800 dp — the common tall phone. One frame: the preview *is* the phone. */
export const FRAME_HEIGHT = 800;

/** Sample values for the live rows; the phone reads these from `QaAgent.status()`. */
const SAMPLE_ROWS: Record<StatusRow, SampleRow> = {
  enrollment: { label: "Enrollment", value: "Enrolled with ACME Ltd" },
  accessibility: { label: "QA service", value: "Off — tap the button above" },
  capture: { label: "Screen capture", value: "streaming via accessibility" },
  version: { label: "Agent", value: "0.4.0" },
  tenant: { label: "Account", value: "ACME Ltd" },
};

const FONT_STACKS = {
  // Roboto first, so the preview's wrapping is as close to the phone's as a
  // browser can get without shipping the font.
  sans: 'Roboto, "Helvetica Neue", ui-sans-serif, system-ui, sans-serif',
  serif: 'Roboto Slab, Georgia, ui-serif, serif',
  mono: 'Roboto Mono, ui-monospace, SFMono-Regular, Menlo, monospace',
} as const;

const WEIGHTS = { regular: 400, medium: 500, bold: 700 } as const;

function textAlign(align: Align | undefined): CSSProperties["textAlign"] {
  return align === "left" ? "left" : align === "right" ? "right" : "center";
}

/** A block element takes a side by its auto margins — what `Gravity.START`
 *  does on the phone for the same `align` (§2 button). */
function autoMargin(align: Align | undefined): string {
  return align === "left" ? "0 auto 0 0" : align === "right" ? "0 0 0 auto" : "0 auto";
}

/**
 * Longhand properties rather than the `background` shorthand: the shorthand
 * with a gradient overlay stacked on a `url()` is exactly the sort of value a
 * strict CSS parser drops on the floor, taking the whole background with it.
 */
export function backgroundStyle(
  background: Background | undefined,
  assets: AssetUrls,
): CSSProperties {
  const value = background ?? DEFAULTS.theme.background;
  if (value.type === "solid") return { backgroundColor: value.color };
  if (value.type === "gradient") {
    // CSS measures a linear gradient the same way the spec does: clockwise
    // from "up", so 180deg runs top to bottom on both sides.
    return {
      backgroundImage: `linear-gradient(${value.angle ?? 180}deg, ${value.colors.join(", ")})`,
    };
  }
  const url = assets[value.asset];
  const layers: string[] = [];
  // The overlay is painted first in CSS order, which puts it *over* the image.
  if (value.overlay) layers.push(`linear-gradient(${value.overlay}, ${value.overlay})`);
  if (url) layers.push(`url("${url}")`);
  return {
    backgroundColor: "#000000",
    backgroundImage: layers.length ? layers.join(", ") : undefined,
    backgroundSize: value.scale ?? "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };
}

/**
 * `example.com` — *which* page this button opens or this block shows, so two
 * of them running the same contribution can be told apart at a glance
 * (MODULES §2.6, §2.11).
 *
 * Read off the values rather than the manifest: the preview is handed the
 * document, not the catalogue, and a link is a link whatever it is called.
 */
function argHost(args: Record<string, unknown> | undefined): string {
  for (const value of Object.values(args ?? {})) {
    if (typeof value !== "string") continue;
    try {
      const { host } = new URL(value);
      if (host) return host;
    } catch {
      // Not a link. The sidebar says so; a tooltip is not the place to.
    }
  }
  return "";
}

/** The same host, as the parenthetical a button's tooltip carries. */
function paramHost(params: Record<string, unknown> | undefined): string {
  const host = argHost(params);
  return host ? ` (${host})` : "";
}

function BlockView({
  block,
  assets,
  sampleRows,
  sampleBlocks,
  disabledModules,
  selected,
  selectedLabel,
  slot,
  draggable,
  onSelect,
}: {
  block: Block;
  assets: AssetUrls;
  sampleRows: Record<string, SampleRow>;
  sampleBlocks: Record<string, SampleBlock>;
  disabledModules: ReadonlySet<string>;
  selected: boolean;
  /** What to call the selected block, on the tag above it. */
  selectedLabel?: string;
  /** The insertion line, when the drop would land against this block. */
  slot?: "before" | "after";
  draggable?: boolean;
  onSelect?: (id: string) => void;
}) {
  const outline: CSSProperties = selected
    ? { outline: "2px solid var(--accent)", outlineOffset: 2, borderRadius: 4 }
    : {};
  const wrap = (node: ReactNode, style: CSSProperties = {}) => (
    <div
      className="design-preview-block"
      data-block-id={block.id}
      data-block-type={block.type}
      data-drop-slot={slot}
      data-pending={block.type === "image" && block.pendingSrc ? "true" : undefined}
      draggable={draggable}
      onDragStart={
        draggable
          ? (event) => {
              event.stopPropagation();
              event.dataTransfer.effectAllowed = "move";
              event.dataTransfer.setData(BLOCK_MIME, block.id);
            }
          : undefined
      }
      onClick={
        onSelect
          ? (event) => {
              // The screen behind is clickable too; a click on a block is the block's.
              event.stopPropagation();
              onSelect(block.id);
            }
          : undefined
      }
      style={{ ...style, ...outline }}
    >
      {/* Which of six things is outlined, said rather than inferred from the
          shape of it. Inert, and counter-scaled by the frame so it is the
          same size at 50 % as at 100 % (`.design-preview-tag`). */}
      {selected && selectedLabel && <span className="design-preview-tag">{selectedLabel}</span>}
      {node}
    </div>
  );

  switch (block.type) {
    case "text": {
      const style: CSSProperties = {
        margin: 0,
        color: block.color ?? DEFAULTS.text.color,
        fontSize: block.size ?? DEFAULTS.text.size,
        fontWeight: WEIGHTS[block.weight ?? DEFAULTS.text.weight],
        fontFamily: FONT_STACKS[block.font ?? DEFAULTS.text.font],
        textAlign: textAlign(block.align ?? DEFAULTS.text.align),
        lineHeight: 1.35,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      };
      // Plain text, never markup — on both sides of the contract (D3).
      return wrap(<p style={style}>{block.text}</p>);
    }

    case "button": {
      const variant = block.variant ?? DEFAULTS.button.variant;
      const fill = block.color ?? DEFAULTS.button.color;
      // A button whose module is switched off is the button the phone will
      // show *disabled* (MODULES §7.1) — the design is intact, the action is
      // not, and the banner above the frame says why.
      const inert =
        block.action.type === MODULE_ACTION && disabledModules.has(block.action.module);
      const full = block.fullWidth ?? DEFAULTS.button.fullWidth;
      const style: CSSProperties = {
        display: "block",
        width: full ? "100%" : "auto",
        // `align` is meaningful only when the button does not fill the row
        // (§2 button); auto margins are how a block element takes a side.
        margin: full ? undefined : autoMargin(block.align ?? DEFAULTS.button.align),
        minHeight: BUTTON_HEIGHTS[block.size ?? DEFAULTS.button.size],
        borderRadius: block.radius ?? DEFAULTS.button.radius,
        color: block.textColor ?? DEFAULTS.button.textColor,
        background: variant === "filled" ? fill : "transparent",
        border: variant === "outlined" ? `1px solid ${fill}` : "1px solid transparent",
        fontFamily: FONT_STACKS.sans,
        fontWeight: 500,
        fontSize: 15,
        padding: "0 16px",
        cursor: onSelect ? "pointer" : "default",
        opacity: inert ? 0.45 : undefined,
      };
      const action =
        block.action.type === MODULE_ACTION
          ? `${block.action.module}.${block.action.action}${paramHost(block.action.params)}`
          : block.action.type;
      return wrap(
        <button type="button" style={style} title={`Action: ${action}`} tabIndex={-1}>
          {block.label}
        </button>,
      );
    }

    case "image": {
      // The object URL of a file still uploading wins over the asset it is
      // replacing, so a dropped picture is on the phone at once (§9.3).
      const url = block.pendingSrc || assets[block.asset];
      const width = (block.fullWidth ?? DEFAULTS.image.fullWidth)
        ? "100%"
        : (block.width ?? DEFAULTS.image.width);
      const style: CSSProperties = {
        width,
        height: "auto",
        borderRadius: block.radius ?? DEFAULTS.image.radius,
        display: "block",
        // Auto margins, not `alignSelf`: the flex child is the wrapper around
        // this element, so a side is taken inside it the way the phone's
        // `Gravity` takes one (§2 button).
        margin: autoMargin(block.align ?? DEFAULTS.image.align),
      };
      if (!url) {
        // A placeholder, not a broken image: the block is still there to be
        // selected and given a picture.
        return wrap(
          <div
            className="design-preview-missing"
            style={{ ...style, height: 72 }}
            aria-label="No image chosen"
          >
            <span>No image</span>
          </div>,
        );
      }
      // `draggable={false}`: the wrapper is the drag source, so a block is
      // reordered by grabbing its picture rather than dragged out as a file.
      return wrap(<img src={url} alt={block.alt ?? ""} style={style} draggable={false} />);
    }

    case "spacer":
      return wrap(<div />, {
        height: block.flex ? undefined : (block.height ?? DEFAULTS.spacer.height),
        flex: block.flex ? "1 1 auto" : undefined,
        minHeight: block.flex ? 8 : undefined,
      });

    case "divider":
      return wrap(
        <div
          style={{
            height: block.thickness ?? DEFAULTS.divider.thickness,
            background: block.color ?? DEFAULTS.divider.color,
            marginLeft: block.inset ?? DEFAULTS.divider.inset,
            marginRight: block.inset ?? DEFAULTS.divider.inset,
          }}
        />,
      );

    case MODULE_BLOCK: {
      // A stand-in for a view only the phone can draw (MODULES §7.4): the
      // module is compiled into the APK, so there is nothing here to render
      // it with. What the preview owes the customer is the *shape* of the
      // screen — how much room this takes, and which page it will be.
      const declared = sampleBlocks[`${block.module}.${block.block}`];
      const flex = isFlexBlock(block, sampleBlocks);
      const inert = disabledModules.has(block.module);
      const host = argHost(block.props);
      return wrap(
        <div
          style={{
            position: "relative",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            overflow: "hidden",
            // A wash and a muted ink over whatever the theme paints, the way
            // the empty-image placeholder is drawn: this is editor chrome
            // standing in for the module's view, not the customer's colours.
            background: "rgba(255, 255, 255, 0.07)",
            color: "rgba(255, 255, 255, 0.55)",
            fontFamily: FONT_STACKS.sans,
            fontSize: 12,
          }}
        >
          {/* The one thing every one of these has in common on the phone: it
              loads. A 2 px bar along the top edge says "page" faster than any
              label would. */}
          <span
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "38%",
              height: 2,
              background: "rgba(255, 255, 255, 0.5)",
            }}
          />
          <IconModuleBlock size={26} />
          <span>{host || declared?.label || `${block.module}.${block.block}`}</span>
        </div>,
        flex
          ? {
              // A child of the frame's own column rather than of a padded
              // stack, so there is nothing here to step out of: it takes the
              // full width and every pixel the runs leave, with no padding
              // and no margin on any side (MODULES §7.4, W10).
              flex: "1 1 0",
              minHeight: 0,
              width: "100%",
              // Faded like a disabled button: the block will be missing on
              // the phone, and the banner above the frame says why (§8.6).
              opacity: inert ? 0.45 : undefined,
            }
          : {
              // Inside the padding, like every other block: `content` is the
              // width the phone gives it too — `DesignRenderer.params` hands
              // a module block `MATCH` in the padded stack, with no side
              // margins of its own, so a text block above it and this one
              // start at the same x. Only `flex` is edge to edge.
              width: "100%",
              flex: "none",
              height: CONTENT_BLOCK_HEIGHT,
              opacity: inert ? 0.45 : undefined,
            },
      );
    }

    case "status": {
      const rows = block.rows?.length ? block.rows : DEFAULTS.status.rows;
      const size = block.size ?? DEFAULTS.status.size;
      return wrap(
        <div style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: size }}>
          {rows.map((row) => {
            // A module row the catalogue cannot name is still shown by its id:
            // the phone skips it, and hiding it here would hide the mistake.
            const sample = SAMPLE_ROWS[row as StatusRow] ??
              sampleRows[row] ?? { label: row, value: "—" };
            return (
              <div key={row} style={{ display: "flex", gap: 6, fontFamily: FONT_STACKS.sans }}>
                <span style={{ color: block.mutedColor ?? DEFAULTS.status.mutedColor }}>
                  {sample.label}
                </span>
                <span style={{ color: block.color ?? DEFAULTS.status.color }}>{sample.value}</span>
              </div>
            );
          })}
        </div>,
      );
    }
  }
}

export interface PhonePreviewProps {
  spec: Spec;
  assets?: AssetUrls;
  /** 1 is life size; the list page's thumbnails use a fraction. */
  scale?: number;
  /** `<module>.<value>` -> the manifest's label and `samples.default`. */
  sampleRows?: Record<string, SampleRow>;
  /** `<module>.<block>` -> the manifest's label and how tall it is. */
  sampleBlocks?: Record<string, SampleBlock>;
  /** Modules the release carries but this customer has switched off. */
  disabledModules?: ReadonlySet<string>;
  selectedId?: string | null;
  /** The selected block's type, for the tag drawn above it. */
  selectedLabel?: string;
  onSelect?: (id: string) => void;
  /** A click on the screen itself, off any block — "edit the screen". */
  onSelectScreen?: () => void;
  /**
   * Files dropped on the frame: over an image block `target` replaces it,
   * otherwise it names the slot between blocks the pointer was in. Absent —
   * as on the list page's thumbnails — the frame takes no drops at all.
   */
  onDropFiles?: (files: File[], target: DropTarget) => void;
  /** A block dragged to a new slot in the stack, 0..blocks.length. */
  onReorder?: (id: string, to: number) => void;
  /** A palette tile dropped on the frame: what it makes, at that slot. */
  onAddBlock?: (item: PaletteItem, at: number) => void;
  /** *Drop to add an image*, in the customer's language. */
  dropLabel?: string;
  /** What an empty design says inside the frame, instead of nothing at all. */
  emptyLabel?: string;
}

const NO_MODULES: ReadonlySet<string> = new Set();

/** What the drag hovering over the frame would do if it were dropped now. */
interface Hover {
  files: boolean;
  slot: number;
  /** The image block under the pointer, which a file would replace. */
  replace: string | null;
}

export default function PhonePreview({
  spec,
  assets = {},
  scale = 1,
  sampleRows = {},
  sampleBlocks = {},
  disabledModules = NO_MODULES,
  selectedId = null,
  selectedLabel,
  onSelect,
  onSelectScreen,
  onDropFiles,
  onReorder,
  onAddBlock,
  dropLabel,
  emptyLabel,
}: PhonePreviewProps) {
  const theme = spec.theme ?? {};
  const height = FRAME_HEIGHT;
  const padding = theme.padding ?? DEFAULTS.theme.padding;
  const stack = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<Hover | null>(null);
  const accepts = !!onDropFiles || !!onReorder || !!onAddBlock;

  /**
   * What a drop here would do. The stack reflows, so the only question a drop
   * can answer is "between which two blocks?" — see `dnd.ts` for why there is
   * no x/y in any of this.
   */
  const read = (event: DragEvent<HTMLElement>): Hover => {
    const rows = Array.from(
      stack.current?.querySelectorAll<HTMLElement>(".design-preview-block") ?? [],
    );
    const files = hasFiles(event.dataTransfer);
    const node = event.target instanceof Element ? event.target : null;
    const over = node?.closest<HTMLElement>('.design-preview-block[data-block-type="image"]');
    return {
      files,
      slot: slotAt(rows, event.clientY),
      replace: files && over ? (over.dataset.blockId ?? null) : null,
    };
  };

  const onDragOver = (event: DragEvent<HTMLElement>) => {
    const files = hasFiles(event.dataTransfer);
    const made = carriesBlockType(event.dataTransfer);
    if (
      !(files && onDropFiles) &&
      !(made && onAddBlock) &&
      !(carriesBlock(event.dataTransfer) && onReorder)
    )
      return;
    // Without this the browser refuses the drop and animates the file back.
    event.preventDefault();
    event.dataTransfer.dropEffect = files || made ? "copy" : "move";
    setHover(read(event));
  };

  const onDrop = (event: DragEvent<HTMLElement>) => {
    if (!accepts) return;
    event.preventDefault();
    const where = read(event);
    setHover(null);
    const moved = draggedBlock(event.dataTransfer);
    if (moved && onReorder) {
      onReorder(moved, where.slot);
      return;
    }
    // A tile from the palette: the same slot a moved block would have taken.
    const made = draggedPaletteItem(event.dataTransfer);
    if (made && onAddBlock) {
      onAddBlock(made, where.slot);
      return;
    }
    const files = filesOf(event.dataTransfer);
    if (files.length > 0 && onDropFiles) {
      const target: DropTarget = where.replace
        ? { kind: "replace", id: where.replace }
        : { kind: "insert", index: where.slot };
      onDropFiles(files, target);
    }
  };

  const slotOf = (index: number, last: boolean): "before" | "after" | undefined => {
    if (!hover || hover.replace !== null) return undefined;
    if (hover.slot === index) return "before";
    return last && hover.slot > index ? "after" : undefined;
  };
  const justify =
    (theme.verticalAlign ?? DEFAULTS.theme.verticalAlign) === "top"
      ? "flex-start"
      : (theme.verticalAlign ?? DEFAULTS.theme.verticalAlign) === "bottom"
        ? "flex-end"
        : "center";

  const blocks = spec.blocks ?? [];
  /** The runs the frame is a column of, or null for the one padded stack a
   *  design without a `flex` block has always been. */
  const runs = blocks.some((block) => isFlexBlock(block, sampleBlocks))
    ? runsOf(blocks, sampleBlocks)
    : null;
  const stackStyle: CSSProperties = {
    padding,
    gap: theme.gap ?? DEFAULTS.theme.gap,
    justifyContent: justify,
  };
  const view = (block: Block, index: number) => (
    <BlockView
      key={block.id}
      block={block}
      assets={assets}
      sampleRows={sampleRows}
      sampleBlocks={sampleBlocks}
      disabledModules={disabledModules}
      selected={block.id === selectedId}
      selectedLabel={selectedLabel}
      slot={slotOf(index, index === blocks.length - 1)}
      draggable={!!onReorder}
      onSelect={onSelect}
    />
  );

  return (
    <div
      className={hover ? "design-phone on" : "design-phone"}
      style={{ width: FRAME_WIDTH * scale, height: height * scale }}
      data-testid="phone-preview"
      onDragOver={accepts ? onDragOver : undefined}
      onDragLeave={
        accepts
          ? (event) => {
              // Moving between the frame's own children is not leaving it.
              const to = event.relatedTarget;
              if (to instanceof Node && event.currentTarget.contains(to)) return;
              setHover(null);
            }
          : undefined
      }
      onDrop={accepts ? onDrop : undefined}
    >
      {hover?.files && dropLabel && (
        /* Over the design rather than in it, and inert: a caption that took
           pointer events would flicker the highlight it belongs to. */
        <p className="design-drop-caption">{dropLabel}</p>
      )}
      <div
        className="design-phone-screen"
        onClick={onSelectScreen}
        style={
          {
            width: FRAME_WIDTH,
            height,
            transform: scale === 1 ? undefined : `scale(${scale})`,
            // The editor's own chrome inside the frame — the selection tag,
            // the empty state — is drawn at its true size by undoing the
            // frame's scale, so a label does not shrink to 7 px at Fit.
            "--design-unscale": String(1 / scale),
            ...backgroundStyle(theme.background, assets),
          } as CSSProperties
        }
      >
        {runs ? (
          /* A design carrying a `flex` block is laid out as the phone lays it
             out (MODULES §7.4, W10): the frame itself is the column, the
             block is a child of it, and what is left of the design is
             gathered into padded runs above and below. No padding here — the
             runs carry the theme's, and the page has none by design. */
          <div ref={stack} className="design-phone-column">
            {runs.map((run) =>
              run.kind === "flex" ? (
                view(run.block, run.at)
              ) : (
                <div className="design-phone-stack" key={`run${run.at}`} style={stackStyle}>
                  {run.blocks.map((block, offset) => view(block, run.at + offset))}
                </div>
              ),
            )}
          </div>
        ) : (
          <div ref={stack} className="design-phone-stack" style={stackStyle}>
            {blocks.map((block, index) => view(block, index))}
            {/* A design with nothing in it is a black rectangle, which looks
                like a bug rather than a beginning. Inert, so the frame under
                it still takes the drop it is asking for. */}
            {emptyLabel && blocks.length === 0 && (
              <p className="design-frame-empty">
                <span>{emptyLabel}</span>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
